import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { TeamService } from '../../../services/teamService';

const AddTeamMemberModal = ({ isOpen, onClose, onAdd }) => {
  const [formData, setFormData] = useState({
    name: '',
    position: '',
    bio: '',
    expertise: []
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [saving, setSaving] = useState(false);

  const handleImageChange = (e) => {
    const file = e?.target?.files?.[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader?.result);
      };
      reader?.readAsDataURL(file);
    }
  };

  const handleExpertiseChange = (value) => {
    const expertiseArray = value?.split(',')?.map(skill => skill?.trim())?.filter(skill => skill?.length > 0);
    setFormData(prev => ({ ...prev, expertise: expertiseArray }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setSaving(true);

    try {
      // Create team member first
      const newMember = await TeamService?.createTeamMember({
        ...formData,
        sort_order: Date.now() // Simple ordering by creation time
      });

      // Upload image if provided
      if (imageFile && newMember?.id) {
        const imageUrl = await TeamService?.uploadPhoto(imageFile, newMember?.id);
        const updatedMember = await TeamService?.updateTeamMember(newMember?.id, { image_url: imageUrl });
        onAdd?.(updatedMember);
      } else {
        onAdd?.(newMember);
      }

      // Reset form
      setFormData({ name: '', position: '', bio: '', expertise: [] });
      setImageFile(null);
      setImagePreview('');
      onClose();
    } catch (error) {
      console.error('Error creating team member:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setFormData({ name: '', position: '', bio: '', expertise: [] });
    setImageFile(null);
    setImagePreview('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-text-primary">Add New Team Member</h2>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors duration-200"
          >
            <Icon name="X" size={20} className="text-gray-500" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Photo Upload */}
          <div className="space-y-3">
            <label className="block text-sm font-semibold text-text-primary">Photo</label>
            <div className="flex items-center space-x-4">
              <div className="w-24 h-24 rounded-full overflow-hidden bg-gray-100 flex items-center justify-center">
                {imagePreview ? (
                  <img src={imagePreview} alt="Preview" className="w-full h-full object-cover" />
                ) : (
                  <Icon name="User" size={32} className="text-gray-400" />
                )}
              </div>
              <label className="cursor-pointer bg-primary text-white px-4 py-2 rounded-lg font-medium hover:bg-primary/90 transition-colors duration-200 flex items-center space-x-2">
                <Icon name="Upload" size={16} />
                <span>Choose Photo</span>
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleImageChange}
                />
              </label>
            </div>
          </div>

          {/* Name */}
          <div className="space-y-2">
            <label className="block text-sm font-semibold text-text-primary">Full Name *</label>
            <input
              type="text"
              value={formData?.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e?.target?.value }))}
              className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-colors duration-200"
              required
              placeholder="Enter full name"
            />
          </div>

          {/* Position */}
          <div className="space-y-2">
            <label className="block text-sm font-semibold text-text-primary">Position *</label>
            <input
              type="text"
              value={formData?.position}
              onChange={(e) => setFormData(prev => ({ ...prev, position: e?.target?.value }))}
              className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-colors duration-200"
              required
              placeholder="e.g., Senior Developer, Marketing Manager"
            />
          </div>

          {/* Bio */}
          <div className="space-y-2">
            <label className="block text-sm font-semibold text-text-primary">Biography</label>
            <textarea
              value={formData?.bio}
              onChange={(e) => setFormData(prev => ({ ...prev, bio: e?.target?.value }))}
              className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-colors duration-200 resize-none"
              rows={4}
              placeholder="Brief description of their background and role..."
            />
          </div>

          {/* Expertise */}
          <div className="space-y-2">
            <label className="block text-sm font-semibold text-text-primary">Areas of Expertise</label>
            <input
              type="text"
              value={formData?.expertise?.join(', ') || ''}
              onChange={(e) => handleExpertiseChange(e?.target?.value)}
              className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-colors duration-200"
              placeholder="Separate skills with commas, e.g., React, Node.js, AWS"
            />
            <p className="text-sm text-gray-500">Separate multiple skills with commas</p>
          </div>

          {/* Actions */}
          <div className="flex space-x-4 pt-4">
            <Button
              type="submit"
              disabled={saving || !formData?.name || !formData?.position}
              className="flex-1"
              iconName="Plus"
              iconPosition="left"
            >
              {saving ? 'Creating...' : 'Add Team Member'}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              className="flex-1"
            >
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddTeamMemberModal;